import { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import type { ProjectDetail } from '../../services/projectService';
import type { SprintItem } from '../../services/sprintService';
import { useSprints, useCompleteSprint } from '../../hooks/useSprints';
import { useTransitionIssue } from '../../hooks/useIssues';
import { usePanelContext } from '../../contexts/PanelContext';
import {
  PRIORITY_CONFIG,
  buildDynamicColumns,
  buildCategoryColumns,
  resolveTargetStatusId,
  getClientAllowedTargets,
  getClientAllowedCategoryTargets,
  type StatusCategory,
  type ProjectRole,
} from '../board/boardUtils';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import { useAllActiveWorkflowStatuses } from '../../hooks/useResolvedWorkflow';
import { useIssueTypeWorkflowMappings, useWorkflowTransitions } from '../../hooks/useWorkflows';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';
import type { WorkflowTransitionsData } from '../../services/workflowService';
import { Info, Ban } from 'lucide-react';
import SprintCompleteModal from './SprintCompleteModal';
import { useAuthStore } from '../../store/authStore';
interface Props {
  project: ProjectDetail;
}

export default function ActiveSprintView({ project }: Props) {
  const { data: sprints = [] } = useSprints(project.id);
  const transitionMutation = useTransitionIssue(project.id);
  const completeMutation = useCompleteSprint(project.id);
  const { openDetailPanel } = usePanelContext();

  // CHG-043: STAKEHOLDER 읽기 전용
  const user = useAuthStore((s) => s.user);
  const myMembership = project.members.find((m) => m.user.id === user?.id);
  const isStakeholder = myMembership?.projectRole === 'STAKEHOLDER' && user?.role !== 'SUPER_ADMIN';

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [activeItem, setActiveItem] = useState<SprintItem | null>(null);

  // CHG-042: 다중 워크플로우 지원
  const { groups: workflowGroups, isMultiWorkflow } = useAllActiveWorkflowStatuses(project.id, project.workflow);
  const { data: mappingData } = useIssueTypeWorkflowMappings(project.id);

  // CHG-043: 전환 규칙 캐싱
  const userProjectRole = (myMembership?.projectRole ?? null) as ProjectRole | null;
  const isSysAdmin = user?.role === 'SUPER_ADMIN';
  const { data: defaultTransitionData } = useWorkflowTransitions(project.workflow.workflowId);
  const transitionDataMap = useMemo(() => {
    const map = new Map<string, WorkflowTransitionsData>();
    if (defaultTransitionData) map.set(project.workflow.workflowId, defaultTransitionData);
    return map;
  }, [defaultTransitionData, project.workflow.workflowId]);

  const getTransitionData = useCallback(
    (issueType: string): WorkflowTransitionsData | null => {
      const override = mappingData?.issueTypeWorkflows.find((m) => m.issueType === issueType);
      const effectiveId = override?.workflow.workflowId ?? project.workflow.workflowId;
      return transitionDataMap.get(effectiveId) ?? null;
    },
    [transitionDataMap, mappingData, project.workflow.workflowId],
  );

  // CHG-043: drag-time allowed targets
  const [allowedTargets, setAllowedTargets] = useState<Map<string, { isAllowed: boolean; disabledReason: string | null }> | null>(null);

  const activeSprint = useMemo(
    () => sprints.find((s) => s.status === 'ACTIVE') ?? null,
    [sprints],
  );

  const statusColumns = useMemo(
    () => buildDynamicColumns(project.workflow.statuses),
    [project.workflow.statuses],
  );

  const categoryColumns = useMemo(() => buildCategoryColumns(), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: isStakeholder ? Infinity : 8 } }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (!activeSprint) return;
      const item = activeSprint.items.find(
        (i) => i.issue.id === event.active.id,
      );
      setActiveItem(item ?? null);

      // CHG-043: compute allowed targets
      if (item) {
        const tData = getTransitionData(item.issue.issueType);
        if (tData && tData.transitionMode === 'RESTRICTED') {
          if (isMultiWorkflow) {
            const targets = new Map<string, { isAllowed: boolean; disabledReason: string | null }>();
            for (const cat of ['TODO', 'IN_PROGRESS', 'DONE'] as StatusCategory[]) {
              if (cat === item.issue.workflowStatus.category) continue;
              const result = getClientAllowedCategoryTargets(item.issue.statusId, cat, tData, userProjectRole, isSysAdmin);
              targets.set(cat, { isAllowed: result.isAllowed, disabledReason: result.disabledReason });
            }
            setAllowedTargets(targets);
          } else {
            const targets = getClientAllowedTargets(item.issue.statusId, tData, userProjectRole, isSysAdmin);
            setAllowedTargets(targets);
          }
        } else {
          setAllowedTargets(null);
        }
      }
    },
    [activeSprint, getTransitionData, isMultiWorkflow, userProjectRole, isSysAdmin],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveItem(null);
      setAllowedTargets(null);
      const { active, over } = event;
      if (!over || !activeSprint) return;

      const issueId = active.id as string;
      const item = activeSprint.items.find((i) => i.issue.id === issueId);
      if (!item) return;

      if (isMultiWorkflow) {
        const targetCategory = over.id as StatusCategory;
        if (item.issue.workflowStatus.category === targetCategory) return;

        // CHG-043: check transition rules
        const tData = getTransitionData(item.issue.issueType);
        if (tData && tData.transitionMode === 'RESTRICTED') {
          const result = getClientAllowedCategoryTargets(item.issue.statusId, targetCategory, tData, userProjectRole, isSysAdmin);
          if (!result.isAllowed) {
            toast.error(result.disabledReason ?? '현재 상태에서 해당 카테고리로 전환할 수 없습니다');
            return;
          }
          if (result.targetStatusId) {
            try {
              await transitionMutation.mutateAsync({ issueId, statusId: result.targetStatusId });
            } catch {
              toast.error('상태 전이에 실패했습니다.');
            }
            return;
          }
        }

        const targetStatusId = resolveTargetStatusId(
          item.issue as any,
          targetCategory,
          workflowGroups,
          mappingData?.issueTypeWorkflows ?? [],
          project.workflow.workflowId,
        );
        if (!targetStatusId) {
          toast.error('대상 카테고리에 해당하는 상태를 찾을 수 없습니다.');
          return;
        }
        try {
          await transitionMutation.mutateAsync({ issueId, statusId: targetStatusId });
        } catch {
          toast.error('상태 전이에 실패했습니다.');
        }
      } else {
        const targetStatusId = over.id as string;
        if (item.issue.statusId === targetStatusId) return;

        // CHG-043: check transition rules
        const tData = getTransitionData(item.issue.issueType);
        if (tData && tData.transitionMode === 'RESTRICTED') {
          const allowedMap = getClientAllowedTargets(item.issue.statusId, tData, userProjectRole, isSysAdmin);
          const target = allowedMap.get(targetStatusId);
          if (!target) {
            toast.error('현재 상태에서 이 상태로 전환할 수 없습니다');
            return;
          }
          if (!target.isAllowed) {
            toast.error(target.disabledReason ?? '전환 권한이 없습니다');
            return;
          }
        }

        try {
          await transitionMutation.mutateAsync({ issueId, statusId: targetStatusId });
        } catch {
          toast.error('상태 전이에 실패했습니다.');
        }
      }
    },
    [activeSprint, transitionMutation, isMultiWorkflow, workflowGroups, mappingData, project.workflow.workflowId, getTransitionData, userProjectRole, isSysAdmin],
  );

  const handleComplete = useCallback(
    (carryOverToSprintId?: string) => {
      if (!activeSprint) return;
      completeMutation.mutate(
        { sprintId: activeSprint.sprintId, data: carryOverToSprintId ? { carryOverToSprintId } : undefined },
        { onSuccess: () => setShowCompleteModal(false) },
      );
    },
    [activeSprint, completeMutation],
  );

  if (!activeSprint) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <p className="text-lg mb-2">활성 스프린트가 없습니다</p>
          <p className="text-sm">백로그에서 스프린트를 시작해주세요</p>
        </div>
      </div>
    );
  }

  const completedCount = activeSprint.items.filter(
    (i) => i.issue.workflowStatus.category === 'DONE',
  ).length;
  const totalCount = activeSprint.items.length;
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const daysLeft = activeSprint.endDate
    ? Math.max(
        0,
        Math.ceil(
          (new Date(activeSprint.endDate).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24),
        ),
      )
    : null;

  const fmtDate = (d: string) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}.${String(dt.getMonth() + 1).padStart(2, '0')}.${String(dt.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Sprint Header */}
      <div className="px-4 py-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">
                {activeSprint.name}
              </h3>
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                활성
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              {activeSprint.startDate && activeSprint.endDate && (
                <span className="text-sm text-gray-500">
                  {fmtDate(activeSprint.startDate)} ~ {fmtDate(activeSprint.endDate)}
                </span>
              )}
              {daysLeft !== null && (
                <span className="text-sm font-medium text-orange-600">
                  D-{daysLeft}
                </span>
              )}
            </div>
            {/* Progress bar */}
            <div className="mt-2 w-[240px]">
              <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                <span>
                  {completedCount}/{totalCount} 완료
                </span>
                <span>{percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCompleteModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              스프린트 종료
            </button>
          </div>
        </div>
      </div>

      {/* Board + Scrum Panel */}
      <div className="flex flex-1 min-h-0">
        {/* Board */}
        <div className="flex-1 min-w-0 overflow-auto p-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {isMultiWorkflow && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg">
                <Info size={16} className="shrink-0" />
                여러 워크플로우가 사용 중입니다. 상태 카테고리 기준으로 표시됩니다.
              </div>
            )}
            <div className="flex gap-3 overflow-x-auto min-h-[400px]">
              {isMultiWorkflow
                ? categoryColumns.map((col) => {
                    const columnItems = activeSprint.items.filter(
                      (i) => i.issue.workflowStatus.category === col.category,
                    );
                    return (
                      <SprintBoardColumn
                        key={col.category}
                        statusId={col.category}
                        label={col.title}
                        color={col.color}
                        count={columnItems.length}
                        activeItem={activeItem}
                        isCategory
                        allowedTargets={allowedTargets}
                      >
                        {columnItems.map((item) => (
                          <DraggableSprintCard
                            key={item.sprintItemId}
                            item={item}
                            onClick={() => openDetailPanel('issue', item.issue.id)}
                            showStatusBadge
                          />
                        ))}
                      </SprintBoardColumn>
                    );
                  })
                : statusColumns.map((col) => {
                    const columnItems = activeSprint.items.filter(
                      (i) => i.issue.statusId === col.id,
                    );
                    return (
                      <SprintBoardColumn
                        key={col.id}
                        statusId={col.id}
                        label={col.title}
                        color={col.color}
                        count={columnItems.length}
                        activeItem={activeItem}
                        allowedTargets={allowedTargets}
                      >
                        {columnItems.map((item) => (
                          <DraggableSprintCard
                            key={item.sprintItemId}
                            item={item}
                            onClick={() => openDetailPanel('issue', item.issue.id)}
                          />
                        ))}
                      </SprintBoardColumn>
                    );
                  })}
            </div>
            <DragOverlay>
              {activeItem && <SprintIssueCard item={activeItem} overlay showStatusBadge={isMultiWorkflow} />}
            </DragOverlay>
          </DndContext>
        </div>

      </div>

      {/* Complete Modal */}
      <SprintCompleteModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        sprint={activeSprint}
        allSprints={sprints}
        onComplete={handleComplete}
        isSubmitting={completeMutation.isPending}
      />
    </div>
  );
}

// --- Board Column ---
function SprintBoardColumn({
  statusId,
  label,
  color,
  count,
  children,
  activeItem,
  isCategory,
  allowedTargets,
}: {
  statusId: string;
  label: string;
  color: string;
  count: number;
  children: React.ReactNode;
  activeItem: SprintItem | null;
  isCategory?: boolean;
  allowedTargets?: Map<string, { isAllowed: boolean; disabledReason: string | null }> | null;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: statusId });

  const isSameColumn = activeItem
    ? isCategory
      ? activeItem.issue.workflowStatus.category === statusId
      : activeItem.issue.statusId === statusId
    : false;

  // CHG-043: check if this column is blocked
  let transitionAllowed: boolean | null = null;
  if (activeItem && !isSameColumn && allowedTargets) {
    const target = allowedTargets.get(statusId);
    transitionAllowed = target?.isAllowed ?? false;
  }

  const isValidTarget = activeItem && !isSameColumn;
  const isBlocked = transitionAllowed === false;

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border-t-4 bg-gray-50 p-3 min-w-[200px] flex-1 transition-all relative ${
        isOver && isValidTarget && !isBlocked ? 'ring-2 ring-primary-500 bg-primary-50/50' : ''
      } ${isOver && (isBlocked || (!isValidTarget && activeItem)) ? 'ring-2 ring-red-300 bg-red-50/30' : ''} ${
        activeItem && !isSameColumn && isBlocked ? 'opacity-50' : ''
      }`}
      style={{ borderTopColor: color }}
    >
      {activeItem && !isSameColumn && isBlocked && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-200/40 rounded-lg pointer-events-none">
          <Ban className="size-8 text-gray-400" />
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        <span className="text-sm text-gray-500 bg-white/70 px-1.5 py-0.5 rounded">
          {count}
        </span>
      </div>
      <div className="space-y-2 min-h-[100px]">{children}</div>
    </div>
  );
}

// --- Draggable card wrapper ---
function DraggableSprintCard({
  item,
  onClick,
  showStatusBadge,
}: {
  item: SprintItem;
  onClick: () => void;
  showStatusBadge?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.issue.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? 'opacity-30' : ''}
    >
      <SprintIssueCard item={item} onClick={onClick} showStatusBadge={showStatusBadge} />
    </div>
  );
}

// --- Sprint Issue Card ---
export function SprintIssueCard({
  item,
  overlay,
  onClick,
  showStatusBadge,
}: {
  item: SprintItem;
  overlay?: boolean;
  onClick?: () => void;
  showStatusBadge?: boolean;
}) {
  const { openProfileModal } = useProfileModal();
  const typeConfig = resolveIssueTypeConfig(item.issue.issueTypeDef, item.issue.issueType);
  const priorityConfig = PRIORITY_CONFIG[item.issue.priority];

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow ${
        overlay ? 'shadow-xl rotate-2' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-mono text-gray-400">
          {item.issue.issueId}
        </span>
        {typeConfig && (
          <typeConfig.Icon size={14} className={typeConfig.color} />
        )}
      </div>
      <div className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
        {item.issue.title}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {priorityConfig && (
            <span className={`text-[11px] ${priorityConfig.color}`}>
              {priorityConfig.label}
            </span>
          )}
          {showStatusBadge && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded text-white"
              style={{ backgroundColor: item.issue.workflowStatus.color }}
            >
              {item.issue.workflowStatus.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {item.issue.storyPoints != null && (
            <span className="text-[11px] bg-gray-100 text-gray-600 px-1 rounded">
              {item.issue.storyPoints}pt
            </span>
          )}
          {item.issue.assignee ? (
            <span onClick={(e) => e.stopPropagation()}>
              <Avatar
                size="xs"
                userId={item.issue.assignee.id}
                name={item.issue.assignee.name}
                avatarUrl={item.issue.assignee.avatarUrl}
                displayColor={item.issue.assignee.displayColor as ProfileColorKey | null | undefined}
                firstName={item.issue.assignee.firstName}
                onClick={() => openProfileModal(item.issue.assignee!.id)}
              />
            </span>
          ) : (
            <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-400 text-[11px] flex items-center justify-center">
              ?
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
